<template>
  <div class="tab-content">
    <!-- Суммарные метрики -->
    <div class="stats-grid stats-grid--3">
      <div class="metric-card metric-card--orders">
        <div class="metric-card__header">
          <span class="metric-card__title">Кол-во заказов</span>
          <span class="metric-card__icon">🚚</span>
        </div>
        <span class="metric-card__value">
          {{ totals.ordersCount }}
        </span>
        <span class="metric-card__sub">за выбранный период</span>
      </div>

      <div class="metric-card metric-card--invoices">
        <div class="metric-card__header">
          <span class="metric-card__title">Сумма заказов</span>
          <span class="metric-card__icon">💼</span>
        </div>
        <span class="metric-card__value">
          {{ formatMoney(totals.ordersTotalAmount) }}
        </span>
        <span class="metric-card__sub">общая сумма</span>
      </div>

      <div class="metric-card metric-card--expenses">
        <div class="metric-card__header">
          <span class="metric-card__title">Расходы</span>
          <span class="metric-card__icon">💸</span>
        </div>
        <span class="metric-card__value">
          {{ formatMoney(totals.expensesAmount) }}
        </span>
        <span class="metric-card__sub">операционные затраты</span>
      </div>
    </div>

    <!-- График динамики -->
    <div v-if="hasChartData" class="card chart-card">
      <div class="chart-card__header">
        <h4>Динамика заказов по периодам</h4>
        <div class="filter-tabs">
          <button
            class="filter-tab"
            :class="{ active: effectiveDynamicChartType === 'line' }"
            :disabled="effectiveDynamicChartType === 'line'"
            @click="dynamicChartType = 'line'"
          >
            Линейный
          </button>
          <button
            class="filter-tab"
            :class="{ active: effectiveDynamicChartType === 'donut' }"
            :disabled="effectiveDynamicChartType === 'donut'"
            @click="dynamicChartType = 'donut'"
          >
            Круговой
          </button>
          <button
            v-if="!barTooManyPoints"
            class="filter-tab"
            :class="{ active: effectiveDynamicChartType === 'bar' }"
            :disabled="effectiveDynamicChartType === 'bar'"
            @click="dynamicChartType = 'bar'"
          >
            Столбчатый
          </button>
        </div>
      </div>

      <LineChart
        v-if="effectiveDynamicChartType === 'line'"
        :series="chartSeries"
        :points="chartPoints"
        :height="240"
        :format-value="formatChartValue"
      />
      <DonutChart
        v-else-if="effectiveDynamicChartType === 'donut'"
        :slices="summaryDonutSlices"
        :format-value="formatMoney"
        center-title="Итого"
      />
      <BarChart
        v-else-if="effectiveDynamicChartType === 'bar'"
        :series="chartSeries"
        :points="chartPoints"
        :height="240"
        :format-value="formatChartValue"
      />
    </div>

    <!-- Заказы по типам -->
    <div v-if="hasOrderTypeData" class="card status-card">
      <div class="status-card__header">
        <h4>Заказы по типам</h4>

        <div class="status-card__header-right">
          <div class="status-card__total">
            Всего:
            <strong>{{ totalOrderTypesCount }} шт.</strong>
            на
            <strong>
              {{ formatMoney(totals.ordersTotalAmount) }}
            </strong>
          </div>

          <div class="filter-tabs">
            <button
              class="filter-tab"
              :class="{ active: orderTypeViewType === 'donut' }"
              :disabled="orderTypeViewType === 'donut'"
              @click="orderTypeViewType = 'donut'"
            >
              Диаграмма
            </button>
            <button
              class="filter-tab"
              :class="{ active: orderTypeViewType === 'bars' }"
              :disabled="orderTypeViewType === 'bars'"
              @click="orderTypeViewType = 'bars'"
            >
              Список
            </button>
          </div>
        </div>
      </div>

      <DonutChart
        v-if="orderTypeViewType === 'donut'"
        :slices="orderTypeDonutSlices"
        :format-value="formatMoney"
        center-title="Итого"
      />
      <PercentageLineChart
        v-else
        :slices="orderTypeDonutSlices"
        :format-value="formatMoney"
      />
    </div>

    <!-- Заказы по статусам оплаты -->
    <div v-if="hasStatusData" class="card status-card">
      <div class="status-card__header">
        <h4>Заказы по статусам оплаты</h4>

        <div class="status-card__header-right">
          <div class="status-card__total">
            Всего:
            <strong>{{ totalOrdersCount }} шт.</strong>
            на
            <strong>
              {{ formatMoney(totals.ordersTotalAmount) }}
            </strong>
          </div>

          <div class="filter-tabs">
            <button
              class="filter-tab"
              :class="{ active: statusViewType === 'donut' }"
              :disabled="statusViewType === 'donut'"
              @click="statusViewType = 'donut'"
            >
              Диаграмма
            </button>
            <button
              class="filter-tab"
              :class="{ active: statusViewType === 'bars' }"
              :disabled="statusViewType === 'bars'"
              @click="statusViewType = 'bars'"
            >
              Список
            </button>
          </div>
        </div>
      </div>

      <DonutChart
        v-if="statusViewType === 'donut'"
        :slices="donutSlices"
        :format-value="formatMoney"
        center-title="Итого"
      />
      <PercentageLineChart
        v-else
        :slices="donutSlices"
        :format-value="formatMoney"
      />
    </div>
  </div>
</template>

<script>
import LineChart from "@/components/Dashboard/Charts/LineChart.vue";
import BarChart from "@/components/Dashboard/Charts/BarChart.vue";
import DonutChart from "@/components/Dashboard/Charts/DonutChart.vue";
import PercentageLineChart from "@/components/Dashboard/Charts/PercentageLineChart.vue";

import {
  formatMoney,
  formatChartValue,
  formatDisplayDate,
} from "@/utils/dashboardFormatters";
import { useReferenceItemsStore } from "@/stores/referenceItemsStore";

const PAYMENT_STATUS_LABELS = {
  not_invoiced: "Не выставлен счёт",
  invoiced: "Счёт выставлен",
  partially_paid: "Частично оплачен",
  paid: "Оплачен",
  overdue: "Просрочен",
  payment_canceled: "Оплата отменена",
};

const PAYMENT_STATUS_COLORS = {
  not_invoiced: "#94a3b8",
  invoiced: "#4c6ef5",
  partially_paid: "#ca8a04",
  paid: "#00bfa5",
  overdue: "#d64747",
  payment_canceled: "#f97316",
};

const CHART_TYPE_KEY = "dashboard:chart:chartType";
const STATUS_VIEW_TYPE_KEY = "dashboard:chart:statusViewType";
const ORDER_TYPE_VIEW_TYPE_KEY = "dashboard:orders:orderTypeViewType";
const VALID_CHART_TYPES = ["line", "bar", "donut"];
const VALID_STATUS_VIEW_TYPES = ["donut", "bars"];
const MAX_BAR_POINTS = 60;
export default {
  name: "DashboardOrdersTab",

  components: {
    LineChart,
    BarChart,
    DonutChart,
    PercentageLineChart,
  },

  props: {
    periods: {
      type: Array,
      required: true,
    },
  },

  data() {
    const savedChartType = localStorage.getItem(CHART_TYPE_KEY);
    const initialChartType = VALID_CHART_TYPES.includes(
      savedChartType
    )
      ? savedChartType
      : "line";

    const savedStatusViewType = localStorage.getItem(
      STATUS_VIEW_TYPE_KEY
    );
    const initialStatusViewType = VALID_STATUS_VIEW_TYPES.includes(
      savedStatusViewType
    )
      ? savedStatusViewType
      : "donut";

    const savedOrderTypeViewType = localStorage.getItem(
      ORDER_TYPE_VIEW_TYPE_KEY
    );
    const initialOrderTypeViewType = VALID_STATUS_VIEW_TYPES.includes(
      savedOrderTypeViewType
    )
      ? savedOrderTypeViewType
      : "donut";

    return {
      dynamicChartType: initialChartType,
      effectiveDynamicChartType: initialChartType,
      statusViewType: initialStatusViewType,
      orderTypeViewType: initialOrderTypeViewType,
      chartSeries: [
        { key: "orders", label: "Сумма заказов", color: "#00bfa5" },
        { key: "expenses", label: "Расходы", color: "#d64747" },
      ],
    };
  },

  computed: {
    hasChartData() {
      return (
        this.periods.length > 1 &&
        this.chartPoints.some((p) =>
          Object.values(p.values).some((v) => v > 0)
        )
      );
    },

    hasStatusData() {
      return this.donutSlices.some((s) => s.value > 0);
    },

    hasOrderTypeData() {
      return this.orderTypeDonutSlices.some((s) => s.value > 0);
    },

    barTooManyPoints() {
      return this.periods.length > MAX_BAR_POINTS;
    },

    totals() {
      return this.periods.reduce(
        (acc, period) => {
          acc.ordersCount += period.orders_count || 0;
          acc.ordersTotalAmount +=
            parseFloat(period.orders_total_amount) || 0;
          acc.expensesAmount +=
            parseFloat(period.expenses_amount) || 0;

          for (const [status, data] of Object.entries(
            period.orders_by_payment_status || {}
          )) {
            if (!acc.ordersByPaymentStatus[status]) {
              acc.ordersByPaymentStatus[status] = {
                count: 0,
                amount: 0,
              };
            }
            acc.ordersByPaymentStatus[status].count +=
              data.count || 0;
            acc.ordersByPaymentStatus[status].amount +=
              parseFloat(data.amount) || 0;
          }

          for (const [orderTypeId, data] of Object.entries(
            period.orders_by_order_type || {}
          )) {
            if (!acc.ordersByOrderType[orderTypeId]) {
              acc.ordersByOrderType[orderTypeId] = {
                count: 0,
                amount: 0,
              };
            }
            acc.ordersByOrderType[orderTypeId].count +=
              data.count || 0;
            acc.ordersByOrderType[orderTypeId].amount +=
              parseFloat(data.amount) || 0;
          }

          return acc;
        },
        {
          ordersCount: 0,
          ordersTotalAmount: 0,
          expensesAmount: 0,
          ordersByPaymentStatus: {},
          ordersByOrderType: {},
        }
      );
    },

    chartPoints() {
      return this.periods.map((p) => ({
        label: formatDisplayDate(p.period_start, p.period_end),
        values: {
          orders: parseFloat(p.orders_total_amount) || 0,
          expenses: parseFloat(p.expenses_amount) || 0,
        },
      }));
    },

    summaryDonutSlices() {
      return [
        {
          key: "orders",
          label: "Сумма заказов",
          color: "#00bfa5",
          value: this.totals.ordersTotalAmount,
          count: null,
        },
        {
          key: "expenses",
          label: "Расходы",
          color: "#d64747",
          value: this.totals.expensesAmount,
          count: null,
        },
      ];
    },

    paymentStatuses() {
      return Object.entries(this.totals.ordersByPaymentStatus).map(
        ([key, data]) => ({
          key,
          label: PAYMENT_STATUS_LABELS[key] || key,
          color: PAYMENT_STATUS_COLORS[key] || "#94a3b8",
          count: data.count,
          amount: data.amount,
        })
      );
    },

    totalOrdersCount() {
      return this.paymentStatuses.reduce(
        (sum, s) => sum + s.count,
        0
      );
    },

    donutSlices() {
      return this.paymentStatuses.map((s) => ({
        key: s.key,
        label: s.label,
        color: s.color,
        value: s.amount,
        count: s.count,
      }));
    },

    referenceItemsStore() {
      return useReferenceItemsStore();
    },

    orderTypeItems() {
      return this.referenceItemsStore.selectItems || [];
    },

    orderTypeItemsById() {
      return this.orderTypeItems.reduce((acc, item) => {
        acc[String(item.id)] = item;
        return acc;
      }, {});
    },

    orderTypes() {
      return Object.entries(this.totals.ordersByOrderType).map(
        ([key, data]) => {
          const item = this.orderTypeItemsById[key];

          return {
            key,
            label: item?.name || this.orderTypeFallbackLabel(key),
            color: item?.color || "#94a3b8",
            count: data.count,
            amount: data.amount,
          };
        }
      );
    },

    totalOrderTypesCount() {
      return this.orderTypes.reduce((sum, s) => sum + s.count, 0);
    },

    orderTypeDonutSlices() {
      return this.orderTypes.map((s) => ({
        key: s.key,
        label: s.label,
        color: s.color,
        value: s.amount,
        count: s.count,
      }));
    },
  },

  watch: {
    dynamicChartType(value) {
      localStorage.setItem(CHART_TYPE_KEY, value);
      if (value === "bar" && this.barTooManyPoints) {
        this.effectiveDynamicChartType = "line";
      } else {
        this.effectiveDynamicChartType = value;
      }
    },

    barTooManyPoints(tooMany) {
      if (tooMany && this.dynamicChartType === "bar") {
        this.effectiveDynamicChartType = "line";
      } else {
        this.effectiveDynamicChartType = this.dynamicChartType;
      }
    },

    statusViewType(value) {
      localStorage.setItem(STATUS_VIEW_TYPE_KEY, value);
    },

    orderTypeViewType(value) {
      localStorage.setItem(ORDER_TYPE_VIEW_TYPE_KEY, value);
    },
  },

  created() {
    if (this.dynamicChartType === "bar" && this.barTooManyPoints) {
      this.effectiveDynamicChartType = "line";
    }
  },

  mounted() {
    this.fetchOrderTypes();
  },

  methods: {
    formatMoney,
    formatChartValue,

    fetchOrderTypes() {
      this.referenceItemsStore.fetchSelectItems({
        targetModel: "Order",
        targetField: "order_type",
      });
    },

    orderTypeFallbackLabel(key) {
      if (key === "without_type") return "Без типа";

      return `Тип #${key}`;
    },
  },
};
</script>

<style scoped>
.tab-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: fadeIn 0.25s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stats-grid {
  display: grid;
  gap: 16px;
}

.stats-grid--3 {
  grid-template-columns: repeat(3, 1fr);
}

.metric-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: var(--base-in-indent);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-top: 3px solid transparent;
  position: relative;
  overflow: hidden;
}

.metric-card::before {
  content: "";
  position: absolute;
  inset: 0;
  opacity: 0.04;
  pointer-events: none;
}

.metric-card--orders {
  border-top-color: #00bfa5;
}
.metric-card--orders::before {
  background: #00bfa5;
}
.metric-card--invoices {
  border-top-color: #4c6ef5;
}
.metric-card--invoices::before {
  background: #4c6ef5;
}
.metric-card--expenses {
  border-top-color: #d64747;
}
.metric-card--expenses::before {
  background: #d64747;
}

.metric-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.metric-card__title {
  font-size: 13px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-card__icon {
  font-size: 22px;
  line-height: 1;
}

.metric-card__value {
  font-size: 28px;
  font-weight: 700;
  color: var(--color-dark);
  line-height: 1;
  letter-spacing: -0.5px;
}

.metric-card__sub {
  font-size: 12px;
  color: #bbb;
  margin-top: auto;
}

.filter-tabs {
  display: flex;
  gap: 4px;
  background: var(--color-grey);
  padding: 3px;
  border-radius: 8px;
  flex-shrink: 0;
}

.filter-tab {
  padding: 7px 16px;
  border: none;
  background: transparent;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  color: #888;
  font-family: "Roboto", sans-serif;
  font-weight: 500;
}

.filter-tab.active {
  background: #fff;
  color: var(--color-dark);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
}

.filter-tab:disabled {
  cursor: default;
  pointer-events: none;
}

.chart-card {
  gap: 16px;
}

.chart-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.status-card {
  gap: 20px;
}

.status-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.status-card__header-right {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.status-card__total {
  font-size: 13px;
  color: #999;
  white-space: nowrap;
}

.status-card__total strong {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-dark);
}

@media screen and (max-width: 1100px) {
  .stats-grid--3 {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media screen and (max-width: 767px) {
  .stats-grid--3 {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .metric-card__value {
    font-size: 22px;
  }

  .chart-card__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .status-card__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .status-card__header-right {
    width: 100%;
    justify-content: space-between;
  }
}

@media screen and (max-width: 480px) {
  .metric-card {
    padding: 14px;
    gap: 8px;
  }
  .metric-card__value {
    font-size: 19px;
  }
  .metric-card__icon {
    font-size: 18px;
  }
}
</style>
